import React, { useState, useEffect, useRef } from "react";
import "./TimePicker.css";

const TimePicker = ({
  initialTime = { hours: 0, minutes: 0, step: 1 },
  onTimeSelected,
}) => {
  const [hours, setHours] = useState(initialTime.hours);
  const [minutes, setMinutes] = useState(initialTime.minutes);
  const hoursRef = useRef(null);
  const minutesRef = useRef(null);
  const isScrolling = useRef(false);
  const scrollTimeout = useRef(null);

  // Generate arrays for hours and minutes
  const hoursArray = Array.from({ length: 24 }, (_, i) => i);
  const minutesArray = createRangeWithStep(0, 59, initialTime.step);

  // Scroll to selected time on initial render
  useEffect(() => {
    if (hoursRef.current) {
      const hourElement = hoursRef.current.children[hours + 1];
      hoursRef.current.scrollTo({
        top:
          hourElement?.offsetTop -
          hoursRef.current.offsetHeight / 2 +
          hourElement?.offsetHeight / 2,
        behavior: "auto",
      });
    }

    if (minutesRef.current) {
      const minuteIndex = minutesArray.findIndex((el) => el == minutes);
      const minuteElement = minutesRef.current.children[minuteIndex + 1];
      if (minuteElement) {
        minutesRef.current.scrollTo({
          top:
            minuteElement?.offsetTop -
            minutesRef.current.offsetHeight / 2 +
            minuteElement?.offsetHeight / 2,
          behavior: "auto",
        });
      }
    }
  }, [hours, minutes]);

  const handleHourScroll = () => {
    if (!hoursRef.current) return;
    // Устанавливаем флаг прокрутки
    isScrolling.current = true;

    // Очищаем предыдущий таймаут
    if (scrollTimeout.current) {
      clearTimeout(scrollTimeout.current);
    }

    // Устанавливаем новый таймаут
    scrollTimeout.current = setTimeout(() => {
      isScrolling.current = false;
      updateSelectedHour();
    }, 150); // Задержка в мс после остановки прокрутки
  };

  const updateSelectedHour = () => {
    if (!hoursRef.current || isScrolling.current) return;

    const middle = hoursRef.current.offsetHeight / 2;
    const items = hoursRef.current.children;

    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      const itemMiddle =
        item.offsetTop - hoursRef.current.scrollTop + item.offsetHeight / 2;

      if (Math.abs(itemMiddle - middle) < item.offsetHeight / 2) {
        const newHour = parseInt(item.getAttribute("data-value") || "0");
        if (newHour !== hours) {
          setHours(newHour);
          // Плавная подстройка к точному положению
          hoursRef.current.scrollTo({
            top:
              item.offsetTop -
              hoursRef.current.offsetHeight / 2 +
              item.offsetHeight / 2,
            behavior: "smooth",
          });
        }
        break;
      }
    }
  };

  const handleMinuteScroll = () => {
    if (!minutesRef.current) return;

    // Устанавливаем флаг прокрутки
    isScrolling.current = true;

    // Очищаем предыдущий таймаут
    if (scrollTimeout.current) {
      clearTimeout(scrollTimeout.current);
    }

    // Устанавливаем новый таймаут
    scrollTimeout.current = setTimeout(() => {
      isScrolling.current = false;
      updateSelectedMinute();
    }, 150); // Та же задержка, что и для часов
  };

  const updateSelectedMinute = () => {
    if (!minutesRef.current || isScrolling.current) return;

    const middle = minutesRef.current.offsetHeight / 2;
    const items = minutesRef.current.children;

    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      const itemMiddle =
        item.offsetTop - minutesRef.current.scrollTop + item.offsetHeight / 2;

      if (Math.abs(itemMiddle - middle) < item.offsetHeight / 2) {
        const newMinute = parseInt(item.getAttribute("data-value") || "0");
        if (newMinute !== minutes) {
          setMinutes(newMinute);
          // Плавная подстройка к точному положению
          minutesRef.current.scrollTo({
            top:
              item.offsetTop -
              minutesRef.current.offsetHeight / 2 +
              item.offsetHeight / 2,
            behavior: "smooth",
          });
        }
        break;
      }
    }
  };

  const handleHourClick = (e) => {
    if (e.target.dataset?.value) {
      setHours(+e.target.dataset.value);
    }
  };

  const handleMinuteClick = (e) => {
    if (e.target.dataset?.value) {
      setMinutes(+e.target.dataset.value);
    }
  };

  const handleConfirm = () => {
    if (onTimeSelected) {
      onTimeSelected({ hours, minutes });
    }
  };

  return (
    <div className="time-picker-container">
      <div className="time-picker">
        <div
          className="time-column hours-column"
          ref={hoursRef}
          onScroll={handleHourScroll}
        >
          <div className="time-item placeholder"></div>
          {hoursArray.map((hour) => (
            <div
              key={`hour-${hour}`}
              className={`time-item ${hour === hours ? "selected" : ""}`}
              data-value={hour}
              onClick={handleHourClick}
            >
              {hour.toString().padStart(2, "0")}
            </div>
          ))}
          <div className="time-item placeholder"></div>
        </div>

        <div className="time-separator">:</div>

        <div
          className="time-column minutes-column"
          ref={minutesRef}
          onScroll={handleMinuteScroll}
        >
          <div className="time-item placeholder"></div>
          {minutesArray.map((minute) => (
            <div
              key={`minute-${minute}`}
              className={`time-item ${minute === minutes ? "selected" : ""}`}
              data-value={minute}
              onClick={handleMinuteClick}
            >
              {minute.toString().padStart(2, "0")}
            </div>
          ))}
          <div className="time-item placeholder"></div>
        </div>

        <div className="time-column minutes-column">
          <button
            type="button"
            className="confirm-button"
            onClick={handleConfirm}
          >
            ок
          </button>
        </div>
        <div className="time-selection-indicator"></div>
      </div>
    </div>
  );
};

export default TimePicker;

const createRangeWithStep = (start, end, step = 1) => {
  const length = Math.floor((end - start) / step) + 1;
  return Array.from({ length }, (_, i) => start + i * step);
};
